import { useFormik } from 'formik';
import * as Yup from 'yup'; // Validation schema
import FormField from "components/FormField";
import MDModal from "components/MDModal";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Select from 'react-select';
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { useCreateUser } from "queries/users";
import { ReqMethods } from "enums";
import Grid from "@mui/material/Grid";
import SoftInput from "components/SoftInput";
import { useAgencyInfo } from "context/agency";
import SoftSelect from "components/SoftSelect";
import { Checkbox, FormControlLabel, FormGroup, IconButton, InputAdornment } from "@mui/material";
import Icon from "components/Icon";
import ReactSelect from "react-select";
import ModelPromptslabels from "../../../UiPrompts/promptlabels/ModelPrompts/Create";
// import { Visibility, VisibilityOff } from "@mui/icons-material";

function ModalComponent({ open, onClose, handleRefresh, accounts, setPage }) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const [agency] = useAgencyInfo();
  const [Loader, setLoader] = useState(false);
  const [ContentResponse, setContentResponse] = useState('');
  const [tags, setTags] = useState([]);
  const [addTagModel, setAddTagModel] = useState(false);
  // const [showPassword, setShowPassword] = useState(false);

  const roleOptions = [
    { label: 'User', value: 'user' },
    { label: 'Admin', value: 'admin' }
  ];

  useEffect(() => {
    setContentResponse('');
    setLoader(false);
  }, [accounts]);

  // promptslabel is another fork's domain (queries/promptLabels.js) - inlined
  // here via useRequest() rather than centralized.
  const { data: tagsoption = [] } = useQuery({
    queryKey: ["prompt-labels", agency?._id],
    queryFn: async () => {
      const [{ response: respAgency }, { response: respAdmin }] = await Promise.all([
        request(ReqMethods.GET, "/promptslabel", {
          query: `type=agency&agency_id=${agency._id}`,
        }),
        request(ReqMethods.GET, "/promptslabel", {
          query: `type=super-admin&agency_id=superYxadminagency_id`,
        }),
      ]);
      return [...(respAgency?.data || []), ...(respAdmin?.data || [])];
    },
    enabled: !!agency?._id,
  });

  const handleChangeTags = (selectedOptions) => {
    setTags(selectedOptions ? selectedOptions?.map(option => option.value) : []);
  };

  const { mutateAsync: createUser } = useCreateUser();

  // promptslabel is another fork's domain - inlined via useRequest().
  const { mutateAsync: createPromptLabel } = useMutation({
    mutationFn: (payload) => request(ReqMethods.POST, "/promptslabel/create", { payload, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["prompt-labels"] }),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      // password: '',
      send_workflow: false,
      account: [],
      role: '',  // Initial value for the role field
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      // password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
      account: Yup.array().min(1, 'At least one account must be selected'),
      tags: Yup.array().min(1, 'At least one tag must be selected'),
      role: Yup.string().required('Role is required'), // Validation for role
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setContentResponse('');

      const payload = {
        userName: values.name,
        prompt_label_ids: tags,
        // password: values.password,
        email: values.email,
        send_workflow: values.send_workflow,
        activeLocation: values.account?.map(item => item.id),
        agency_id: agency._id,
        role: values.role, // Set role from form values
      };

      const { response, error } = await createUser(payload);

      if (response) {
        setContentResponse(response.message);
        setLoader(false);
        // The list is already sorted newest-first by the backend - jump back
        // to page 1 so the user actually sees the one they just created,
        // even if they were sitting on a later page when they created it.
        setPage?.(1);
        onClose();
      } else {
        setContentResponse(error.message);
        setLoader(false);
      }
    },
  });

  const savedataPromptLabel = async (formData) => {
    const updatedFormData = {
      ...formData,
      agency_id: agency._id,
      type: "agency"
    };
    const { response } = await createPromptLabel(updatedFormData);
    if (response) {
      handleclose();
      setAddTagModel(false);
    }
  };

  const handleclose = () => {
    setAddTagModel(false);
  };

  const handleAddTag = () => {
    setAddTagModel(true);
  };

  return (
    <>
      {open && (
        <div className="modal-zz-confirm text-left">
          <div className="scrollable-modal-content bg-white rounded" style={{ position: "relative" }}>
            <h2 className='text-black text-left font-14'>Create User</h2>
            <span className='crossison' onClick={onClose}>X</span>
            {ContentResponse !== "" && (
              <p className="text-center" style={{ width: "100%", background: "skyblue", color: "darkblue", padding: "5px", borderRadius: "5px", border: "1px solid darkblue" }}>
                {ContentResponse}
              </p>
            )}
            <form component="form" onSubmit={formik.handleSubmit}>
              <SoftBox mb={1} sx={{ fontSize: "14px", fontWeight: "500" }}>
                <SoftTypography variant="button" mb={1}>
                  <span className="text-black">Account</span>
                </SoftTypography>
                <Select
                  isMulti={true}
                  value={formik.values.account}  // Bind formik value
                  onChange={(selectedOptions) => formik.setFieldValue('account', selectedOptions)}  // Update formik onChange
                  name="account"
                  options={accounts.filter(item => item.business_name !== "Bevis")}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.account && formik.errors.account ? (
                  <small className="error" style={{ color: 'red', fontSize: 'small' }}>
                    {formik.errors.account}
                  </small>
                ) : null}
              </SoftBox>


              <SoftBox className="margin-top-10px">
                <SoftTypography className="text-black w-100 d-flex justify-content-between" variant="button" sx={{ fontSize: "14px", fontWeight: "500" }} mb={1}>
                  <label className='w-50 new_dezin_label text-black '>Tags</label>
                  {/* <p className='w-50 text-right new_dezin_label text-black cursor-pointer' onClick={handleAddTag}>+ Add Tag</p> */}
                </SoftTypography>
                <ReactSelect
                  name='tags'
                  className="form-input w-full"
                  options={Array.isArray(tagsoption) ? tagsoption.filter(item => item.api_type === 'user').map((item) => ({
                    label: item?.template?.templatename ? item?.template?.templatename : item?.name,
                    value: item._id,
                  })) : []}
                  isMulti={true}
                  onChange={handleChangeTags}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.tags && formik.errors.tags ? (
                  <small className="error" style={{ color: 'red', fontSize: 'small' }}>{formik.errors.tags}</small>
                ) : null}
              </SoftBox>

              <SoftBox mb={1}>
                <SoftTypography className="text-black" variant="button" sx={{ fontSize: "14px", fontWeight: "500" }} mb={1}>
                  Name
                </SoftTypography>
                <SoftInput
                  label="Name"
                  placeholder="Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {formik.touched.name && formik.errors.name ? (
                  <small className="error" style={{ color: 'red', fontSize: 'small' }}>{formik.errors.name}</small>
                ) : null}
              </SoftBox>

              <SoftBox mb={1}>
                <SoftTypography className="text-black" variant="button" sx={{ fontSize: "14px", fontWeight: "500" }} mb={1}>
                  Email
                </SoftTypography>
                <SoftInput
                  label="Email"
                  placeholder="Email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                />
                {formik.touched.email && formik.errors.email ? (
                  <small className="error" style={{ color: 'red', fontSize: 'small' }}>{formik.errors.email}</small>
                ) : null}
              </SoftBox>

              {/* <SoftBox mb={1}>
                <SoftTypography
                  className="text-black"
                  variant="button"
                  sx={{ fontSize: "14px", fontWeight: "500" }}
                  mb={1}
                >
                  Password
                </SoftTypography>
                <SoftInput
                  type={showPassword ? "text" : "password"} 
                  label="Password"
                  placeholder="Password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  
                  endAdornment={
                    <InputAdornment style={{position:"absolute",right:"0px"}}>
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)} 
                        onMouseDown={(e) => e.preventDefault()} 
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {formik.touched.password && formik.errors.password ? (
                  <small className="error" style={{ color: "red", fontSize: "small" }}>
                    {formik.errors.password}
                  </small>
                ) : null}
              </SoftBox> */}

              {/* New Role Select Field */}
              <SoftBox mb={1}>
                <SoftTypography className="text-black" variant="button" sx={{ fontSize: "14px", fontWeight: "500" }} mb={1}>
                  Role
                </SoftTypography>
                <ReactSelect
                  name='role'
                  className="form-input w-full"
                  options={roleOptions}
                  onChange={(option) => formik.setFieldValue('role', option?.value)}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.role && formik.errors.role ? (
                  <small className="error" style={{ color: 'red', fontSize: 'small' }}>{formik.errors.role}</small>
                ) : null}
              </SoftBox>

              <SoftBox mb={1}>
                <FormGroup style={{ marginLeft: "10px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="send_workflow"
                        checked={formik.values.send_workflow}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Send Workflow"
                  />
                </FormGroup>
              </SoftBox>

              <SoftBox mt={4}>
                <button className='savebtn-setting w-100 position-relative' type='sbumit' disabled={Loader} color="dark" fullWidth>
                  {Loader && (
                    <Icon id="spinner" color="#fff" />
                  )}
                  <span style={{ marginLeft: '6px' }}>Add</span>
                </button>
              </SoftBox>
            </form>
          </div>
        </div>
      )}
      {addTagModel && (
        <ModelPromptslabels data={null} modelfunctionality={savedataPromptLabel} openModel={addTagModel} onclosemodel={handleclose} />
      )}
    </>
  );
}

ModalComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func,
  accounts: PropTypes.array,
  setPage: PropTypes.func,
};

function Modal({ handleRefresh, accounts, setPage }) {
  const [openAddMember, setOpenAddMember] = useState(false);

  return (
    <>
      <button className="btn btn-outline-dark" onClick={() => setOpenAddMember(true)}>
        Create User
      </button>

      <ModalComponent
        open={openAddMember}
        accounts={accounts}
        onClose={() => setOpenAddMember(false)}
        handleRefresh={handleRefresh}
        setPage={setPage}
      />
    </>
  );
}

Modal.propTypes = {
  handleRefresh: PropTypes.func,
  accounts: PropTypes.array,
  setPage: PropTypes.func,
};

export default Modal;
